import type { BlogArticle } from '@/lib/blog-data'

export const post: BlogArticle = {
    slug: 'tesla-model-s-custom-sounds',
    title: 'Tesla Model S Custom Lock Sound: How to Change It (2026)',
    excerpt: 'Change the lock sound on a Tesla Model S (2021 refresh & later, including Plaid): exact USB path (Boombox/LockChime.wav), the Toybox menu, and how to swap sounds later.',
    image: '/images/blog/generated/tesla-model-s-custom-sounds.png',
    content: `
# Tesla Model S Custom Lock Sound: How to Change It

Want to change the custom lock sound on your Tesla Model S? Put a WAV file named \`LockChime.wav\` at \`Boombox/LockChime.wav\` on a FAT32 or exFAT USB drive, plug the drive into a USB port that carries data (the glovebox port on newer cars), and open **Toybox → Boombox → Lock Sound, then choose USB**. That's the whole install. Swapping to a different sound later is one file copy.

This page is the Model S spoke. For the all-model hub, open the main [Tesla lock sound install guide](/guide). If you just need a file that already works, browse [Model S-ready lock sounds](/sounds/model/model-s) or jump to [direct LockChime.wav downloads](/download).

## Quick Answer: Change the Model S Lock Sound

1. Download a sound. Every file here is already named **LockChime.wav**.
2. Format a USB drive as **FAT32 or exFAT** (not APFS/NTFS).
3. Create the folder path **Boombox/LockChime.wav** on the drive.
4. Insert the drive into a **USB port that carries data** (the glovebox port on newer cars).
5. On the touchscreen: **Toybox → Boombox → Lock Sound, then choose USB**.
6. Lock the car once to test.

## Which Model S Supports Custom Lock Sounds

The custom lock sound plays through the external pedestrian-warning speaker, so the hardware decides:

- **Model S 2021 refresh or later**: supported. This includes both **Long Range and Plaid**; the feature is identical across trims.
- **Pre-2021 Model S**: not supported. Those cars don't have the external pedestrian-warning speaker, and a software update alone won't add the feature.
- **Software**: custom lock sounds need the external speaker (Model S from the 2021 refresh) and software 2023.44 or later. If you don't see the Lock Sound option, update from **Controls → Software**.

## Step-by-Step Installation

### 1. Prepare the USB Drive
Format a USB drive as FAT32 or exFAT. If your Model S came with Tesla's factory USB drive, you can reuse it: add a \`Boombox\` folder next to the existing \`TeslaCam\` folder (the dashcam keeps working).

### 2. Create the Folder Structure
\`\`\`
USB Drive/
└── Boombox/
    └── LockChime.wav
\`\`\`
The filename is case-sensitive: capital L, capital C.

### 3. Add Your Sound File
Download a sound from the [Model S library](/sounds/model/model-s) or the [direct downloads page](/download): every file is already named \`LockChime.wav\` and exported at the recommended 44.1 kHz / 16-bit spec. Place it inside the Boombox folder.

### 4. Select It in the Car
1. Insert the drive into a **USB port that carries data** (the glovebox port on newer cars).
2. Open **Toybox**
3. Tap **Boombox**
4. Select **Lock Sound**
5. Choose **USB**

### 5. Test
Lock your Model S with the app, key fob, or walk-away lock. You should hear your custom sound from the external speaker.

## Already Have a Custom Sound? How to Change It

Changing an installed sound is faster than the first install, because the menu is already set to USB:

1. Take the USB drive out and plug it into your computer.
2. Replace \`Boombox/LockChime.wav\` with the new file (same name, same folder).
3. Reinsert the drive. The next lock plays the new sound.

If the old sound keeps playing, re-open **Toybox → Boombox → Lock Sound** and re-select **USB** so the car re-reads the file.

## How to Go Back to the Stock Sound

Open **Toybox → Boombox → Lock Sound** and switch the source from **USB** back to **Default**. The drive can stay plugged in; the file is ignored until you select USB again. Full details in the [disable / revert guide](/blog/how-to-disable-tesla-lock-sound).

## Model S-Specific Notes

- **Picking a sound.** We have no published speaker specs for the Model S, so preview a sound before you commit. Start from the [best Model S lock sounds](/blog/best-tesla-model-s-lock-sounds) shortlist.
- **Plaid is identical.** Same menu, same file path. See the [Model S Plaid guide](/blog/tesla-model-s-plaid-lock-sound) for Plaid-specific picks.
- **Volume.** The external speaker volume lives under **Controls → Safety → External Speaker**; turn it up if the sound is hard to hear across a parking lot.

## Troubleshooting

### USB doesn't appear in the Boombox menu
- Confirm the drive is FAT32 or exFAT
- Confirm the exact path is \`Boombox/LockChime.wav\`
- Try another USB port that carries data
- Remove and reinsert the drive before reopening Boombox

### The car still plays the stock chime
- Re-open **Toybox → Boombox → Lock Sound** and make sure the source is **USB**, not Default
- Lock the car once: the sound only plays on lock, not when you change the setting

### The file looks right but still fails
- Recheck that it's a real 44.1 kHz, 16-bit PCM WAV, not a renamed MP3
- Confirm the filename is exactly \`LockChime.wav\`
- Test with a known-good file from [direct downloads](/download), or re-export yours through the [audio converter](/convert)
- Still stuck? The [troubleshooting guide](/blog/troubleshooting-tesla-custom-sounds) covers every failure mode

## FAQ

### How do I change the custom lock sound on my Tesla Model S?
Put a WAV named \`LockChime.wav\` at \`Boombox/LockChime.wav\` on a FAT32 or exFAT USB drive, plug it into a USB port that carries data (the glovebox port on newer cars), and open **Toybox → Boombox → Lock Sound, then choose USB**. To change it later, replace the file on the drive with a new one using the same name and path.

### Does the Model S Plaid support custom lock sounds?
Yes. Plaid works the same as any 2021-refresh Model S: same USB path, same Boombox menu. Keep software current under Controls → Software.

### Can a pre-2021 Model S get a custom lock sound?
No. The sound plays through the external pedestrian-warning speaker, which pre-2021 Model S cars don't have. A software update alone won't add the feature; the 2021 refresh or later is required.

### Why isn't my Model S lock sound changing after I swapped the file?
Usually one of four things: the drive format (must be FAT32/exFAT), the exact path (\`Boombox/LockChime.wav\`), the filename case, or the Boombox source still reading a cached file. Reinsert the drive and re-select **USB** under Toybox → Boombox → Lock Sound.

### What file format does the Model S need?
A WAV file named exactly \`LockChime.wav\`. For clean playback we recommend 44.1 kHz, 16-bit PCM, 1–5 seconds, under 1 MB. Every download on this site is already exported that way.

## See Also

- [Model S lock sounds library](/sounds/model/model-s) - Browse every Model S-compatible sound
- [Best Tesla Model S lock sounds](/blog/best-tesla-model-s-lock-sounds) - Our Model S shortlist
- [Model S Plaid lock sound guide](/blog/tesla-model-s-plaid-lock-sound) - Plaid-specific setup and picks
- [Tesla lock sound install guide](/guide) - All-model USB hub
- [USB format: FAT32 vs exFAT](/blog/tesla-usb-format-lock-sound-fat32-exfat) - Get the drive right
- [LockChime.wav spec guide](/lockchime) - Exact file-name and format rules
- [Troubleshooting guide](/blog/troubleshooting-tesla-custom-sounds) - Fix USB misses and fallback chimes
- [Direct LockChime.wav downloads](/download) - Fastest install-ready file path
    `,
    author: 'Tesla Lock Sound Team',
    publishedAt: '2026-01-06',
    updatedAt: '2026-07-17',
    category: 'model-guides',
    tags: ['model-s', 'guide', 'installation', 'change', 'custom lock sound', 'tesla model s custom lock sound change', 'plaid'],
    readingTime: 5,
    soundCategory: 'trending',
    soundLimit: 4,
    featured: false
  }
